import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import {
  MdDownload, MdPictureAsPdf,
  MdTableChart, MdRefresh,
} from "react-icons/md";
import {
  BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";

const Reports = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [to, setTo] = useState(new Date().toISOString().split("T")[0]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/reports?from=${from}&to=${to}`);
      setReport(res.data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExportCSV = async () => {
    try {
      const response = await API.get(`/reports/export/csv?from=${from}&to=${to}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `network-report-${from}-to-${to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export CSV failed:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await API.get(`/reports/export/pdf?from=${from}&to=${to}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `network-report-${from}-to-${to}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export PDF failed:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  const chartData = report?.summary?.map((d) => ({
    name: d.name,
    uptime: parseFloat(d.uptime),
    latency: parseFloat(d.avgLatency),
    alerts: d.totalAlerts,
  }));

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Reports</h2>
          <p className="text-sm text-gray-500">
            Network performance reports and exports
          </p>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm"
          >
            <MdTableChart size={18} className="text-green-600" />
            Export CSV
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
          >
            <MdPictureAsPdf size={18} />
            Export PDF
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mt-4">
            <button
              onClick={fetchReport}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
            >
              <MdRefresh size={18} />
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">
          Generating report...
        </div>
      ) : !report ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">
          No report data available
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Uptime Chart */}
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Device Uptime (%)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="uptime" fill="#2563eb" name="Uptime %" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Alerts Chart */}
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Alerts Per Device
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="alerts" fill="#dc2626" name="Total Alerts" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary Table */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
              <MdDownload size={18} className="text-gray-500" />
              <h3 className="text-md font-semibold text-gray-700">
                Device Performance Summary
              </h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3 text-left">Device</th>
                  <th className="px-6 py-3 text-left">IP Address</th>
                  <th className="px-6 py-3 text-left">Type</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Uptime</th>
                  <th className="px-6 py-3 text-left">Avg Latency</th>
                  <th className="px-6 py-3 text-left">Alerts</th>
                  <th className="px-6 py-3 text-left">Critical</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.summary.map((device) => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {device.name}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {device.ipAddress}
                    </td>
                    <td className="px-6 py-4 text-gray-600 capitalize">
                      {device.type.replace("_", " ")}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          device.currentStatus === "ONLINE"
                            ? "bg-green-100 text-green-700"
                            : device.currentStatus === "OFFLINE"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {device.currentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{device.uptime}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {device.avgLatency}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {device.totalAlerts}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-xs font-medium ${
                          device.criticalAlerts > 0
                            ? "text-red-600"
                            : "text-gray-500"
                        }`}
                      >
                        {device.criticalAlerts}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
};

export default Reports;